// apps/merchant-v3/src/hooks/useAnnouncements.ts
//
// Phase 8.3 amend #2 — merchant-portal hook that
// fetches active announcements from the new public endpoint
// `GET /api/v1/announcements?portal=merchant&surface=...&category=...`.
//
// The hook returns the rows for the merchant portal scoped to the caller-
// supplied `surface` (topbar / page:orders / page:dashboard / …) +
// optional `category` filter. Dismissed rows are still returned so the
// component can decide whether to re-show on a new mount; the
// `force_show` flag bypasses the dismiss state.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';

export type AnnouncementSurface =
  | 'topbar'
  | 'page:orders'
  | 'page:dashboard'
  | 'page:products'
  | 'login'
  | 'homepage';

export type AnnouncementCategory =
  | 'system_notice'
  | 'promo_marketing'
  | 'maintenance'
  | 'feature_release';

export type AnnouncementPriority = 'low' | 'medium' | 'high' | 'critical';

export interface AnnouncementI18nString {
  en: string;
  vi?: string;
  fr?: string;
}

export interface Announcement {
  id: string;
  title_i18n: AnnouncementI18nString;
  body_i18n: AnnouncementI18nString;
  category: AnnouncementCategory;
  surface: AnnouncementSurface[];
  priority: AnnouncementPriority;
  schedule_start: string | null;
  schedule_end: string | null;
  dismissible: boolean;
  force_show: boolean;
}

export interface UseAnnouncementsParams {
  surface: AnnouncementSurface;
  category?: AnnouncementCategory;
}

export function useAnnouncements(
  params: UseAnnouncementsParams,
): UseQueryResult<Announcement[]> {
  const { surface, category } = params;
  return useQuery({
    queryKey: ['announcements', 'merchant', surface, category ?? null],
    queryFn: async (): Promise<Announcement[]> => {
      const qs = new URLSearchParams({ portal: 'merchant', surface });
      if (category) qs.set('category', category);
      // apiClient unwraps `{ ok:true, data }` → returns `data` directly.
      const rows = await apiClient.get<Announcement[]>(
        `/api/v1/announcements?${qs.toString()}`,
      );
      return Array.isArray(rows) ? rows : [];
    },
    // 60s — banners change infrequently; WS push (`system.announcement.new`)
    // would invalidate sooner if wired.
    staleTime: 60_000,
    retry: 1,
  });
}

/**
 * Per-announcement localStorage dismiss state.
 * Key: `announcement.{id}.dismissed`.
 */
export function isAnnouncementDismissed(id: string): boolean {
  try {
    return localStorage.getItem(`announcement.${id}.dismissed`) === '1';
  } catch {
    return false;
  }
}

export function dismissAnnouncement(id: string): void {
  try {
    localStorage.setItem(`announcement.${id}.dismissed`, '1');
  } catch {
    /* localStorage may be unavailable in private mode — fail silent */
  }
}
